/* eslint-disable react-hooks/rules-of-hooks */
// Copied from https://github.com/leny/react-use-storage/blob/master/src/index.js
import { useEffect, useCallback } from 'react';
import { BroadcastChannel } from 'broadcast-channel';
import { useRecoilState, atomFamily, RecoilState } from 'recoil';
import { nanoid } from 'nanoid';

const bc = new BroadcastChannel('use-storage-hook');

type StorageAtomFamily = (key: string) => RecoilState<any>;

const atoms = new Map<Storage, StorageAtomFamily>();

const getAtom = (storage: Storage): StorageAtomFamily => {
  if (atoms.has(storage)) {
    return atoms.get(storage)!;
  }

  const atomFamilyInstance = atomFamily({
    key: nanoid(),
    default: (key: string) => {
      const item = storage.getItem(key);
      return item ? JSON.parse(item) : null;
    },
  });

  atoms.set(storage, atomFamilyInstance);
  return atomFamilyInstance;
};

type UpdaterFunction<T> = (oldValue: T) => T;
type MessageDetail = { key: string };

const useStorage = (storage: Storage) => (key: string, defaultValue: string | null) => {
  const raw = storage.getItem(key);

  const [_value, setValue] = useRecoilState(getAtom(storage)(key));
  const value = _value ?? defaultValue;

  const updater = useCallback(
    (updatedValue: any | UpdaterFunction<any>, remove = false) => {
      setValue((oldValue: any) => {
        let newValue = updatedValue;
        if (typeof updatedValue === 'function') {
          newValue = updatedValue(oldValue);
        }
        storage[remove ? 'removeItem' : 'setItem'](key, JSON.stringify(newValue));
        bc.postMessage({ detail: { key } });
        return newValue;
      });
    },
    [key, setValue]
  );

  useEffect(() => {
    defaultValue != null && !raw && updater(defaultValue);
  }, [defaultValue, raw, updater]);

  useEffect(() => {
    const listener = ({ detail }: { detail: MessageDetail }) => {
      if (detail.key === key) {
        const lraw = storage.getItem(key);
        if (lraw !== raw && lraw !== null) {
          setValue(JSON.parse(lraw));
        }
      }
    };
    bc.addEventListener('message', listener);
    return () => bc.removeEventListener('message', listener);
  });

  const reset = useCallback(() => updater(null, true), [updater]);

  return [value, updater, reset];
};

export const useLocalStorage = useStorage(localStorage);
export const useSessionStorage = useStorage(sessionStorage);
